import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { AdminLayout } from '../../layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StatusBadge } from '../../components/LiveIndicator';
import { 
  Trophy, 
  Users, 
  DollarSign, 
  TrendingUp,
  Plus,
  ArrowRight,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export function AdminDashboard() {
  const [stats, setStats] = useState({
    races: [],
    users: [],
    exposure: null,
    pnl: null
  });
  const [loading, setLoading] = useState(true);
  const { subscribeToType } = useWebSocket();

  const fetchData = async () => {
    try {
      const [racesRes, usersRes, exposureRes, pnlRes] = await Promise.all([
        axios.get(`${API}/races`),
        axios.get(`${API}/users`),
        axios.get(`${API}/exposure/system`),
        axios.get(`${API}/reports/pnl`)
      ]);
      
      setStats({
        races: racesRes.data,
        users: usersRes.data,
        exposure: exposureRes.data,
        pnl: pnlRes.data
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Subscribe to real-time updates
    const unsubscribe = subscribeToType('*', (data) => {
      if (['race_created', 'race_status_changed', 'bet_placed', 'bet_cancelled', 'race_settled'].includes(data.type)) {
        fetchData();
      }
    });

    return unsubscribe;
  }, [subscribeToType]);

  const activeRaces = stats.races.filter(r => r.status === 'open').length;
  const traderCount = stats.users.filter(u => u.role === 'trader').length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-zinc-100">Dashboard</h1>
            <p className="text-sm text-zinc-500 mt-1">Overview of your trading system</p>
          </div>
          <Link to="/admin/races">
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-race-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Race
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">Active Races</CardTitle>
              <Trophy className="w-4 h-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="font-mono text-2xl font-bold text-zinc-100" data-testid="active-races-count">
                {activeRaces}
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                {stats.races.length} total races
              </p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">Traders</CardTitle>
              <Users className="w-4 h-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="font-mono text-2xl font-bold text-zinc-100" data-testid="traders-count">
                {traderCount}
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Active trader accounts
              </p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">Total Stakes</CardTitle>
              <DollarSign className="w-4 h-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="font-mono text-2xl font-bold text-zinc-100" data-testid="total-stakes">
                €{stats.exposure?.total_stakes?.toFixed(2) || '0.00'}
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                From {stats.exposure?.open_bets || 0} open bets
              </p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-zinc-400">Max Liability</CardTitle>
              <TrendingUp className="w-4 h-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="font-mono text-2xl font-bold text-zinc-100" data-testid="max-liability">
                €{stats.exposure?.total_potential_payout?.toFixed(2) || '0.00'}
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Potential payout exposure
              </p>
            </CardContent>
          </Card>
        </div>

        {/* P&L Summary */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading text-zinc-100 flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" />
              Profit & Loss
            </CardTitle>
            {stats.pnl?.settled_races > 0 && (
              <span className="text-xs text-zinc-500">{stats.pnl.settled_races} settled race{stats.pnl.settled_races !== 1 ? 's' : ''}</span>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="p-3 bg-zinc-800/50 rounded border border-zinc-800 text-center">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Collected</p>
                <p className="font-mono text-xl font-bold text-emerald-400" data-testid="pnl-collected">
                  €{stats.pnl?.total_collected?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded border border-zinc-800 text-center">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Paid Out</p>
                <p className="font-mono text-xl font-bold text-red-400" data-testid="pnl-paid-out">
                  €{stats.pnl?.total_paid_out?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className={`p-3 rounded border text-center ${
                (stats.pnl?.net_profit || 0) >= 0 
                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                  : 'bg-red-500/10 border-red-500/30'
              }`}>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Net P&L</p>
                <p className={`font-mono text-xl font-bold ${
                  (stats.pnl?.net_profit || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`} data-testid="pnl-net">
                  €{stats.pnl?.net_profit?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded border border-zinc-800 text-center">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Open Stakes</p>
                <p className="font-mono text-xl font-bold text-yellow-400" data-testid="pnl-open-stakes">
                  €{stats.pnl?.open_stakes?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded border border-zinc-800 text-center">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Open Liability</p>
                <p className="font-mono text-xl font-bold text-red-400" data-testid="pnl-open-liability">
                  €{stats.pnl?.open_liability?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
            {/* Per-race P&L breakdown */}
            {stats.pnl?.races?.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {stats.pnl.races.map(race => (
                  <div 
                    key={race.race_id}
                    className="flex items-center justify-between p-2.5 bg-zinc-800/30 rounded border border-zinc-800"
                  >
                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium text-zinc-100">{race.race_name}</span>
                      <span className="text-xs text-zinc-500">{race.total_bets} bets</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono text-xs text-zinc-400">
                        In: €{race.total_collected.toFixed(2)}
                      </span>
                      <span className="font-mono text-xs text-zinc-400">
                        Out: €{race.total_paid_out.toFixed(2)}
                      </span>
                      <span className={`font-mono text-sm font-bold ${
                        race.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'
                      }`}>
                        {race.net_profit >= 0 ? '+' : ''}€{race.net_profit.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Races */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading text-zinc-100">Recent Races</CardTitle>
              <Link to="/admin/races" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-12 bg-zinc-800 animate-pulse rounded" />
                  ))}
                </div>
              ) : stats.races.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <Trophy className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No races created yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {stats.races.slice(0, 5).map(race => (
                    <div 
                      key={race.id} 
                      className="flex items-center justify-between p-3 bg-zinc-800/50 rounded border border-zinc-800"
                      data-testid={`race-item-${race.id}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-zinc-100">{race.venue}</p>
                        <p className="text-xs text-zinc-500">{race.time} • {new Date(race.date).toLocaleDateString()}</p>
                      </div>
                      <StatusBadge status={race.status} />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Exposure by Race */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading text-zinc-100">Exposure by Race</CardTitle>
              <Link to="/admin/exposure" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                Details <ArrowRight className="w-3 h-3" />
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-12 bg-zinc-800 animate-pulse rounded" />
                  ))}
                </div>
              ) : !stats.exposure?.races?.length ? (
                <div className="text-center py-8 text-zinc-500">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No active exposure</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {stats.exposure.races.slice(0, 5).map(race => (
                    <div 
                      key={race.race_id} 
                      className="flex items-center justify-between p-3 bg-zinc-800/50 rounded border border-zinc-800"
                    >
                      <div>
                        <p className="text-sm font-medium text-zinc-100">{race.race_name}</p>
                        <p className="text-xs text-zinc-500">{race.bet_count} bets</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-sm font-bold text-zinc-100">€{race.total_stakes.toFixed(2)}</p>
                        <p className="text-xs text-red-400">Max: €{race.max_liability.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
